import { connectionStatus } from "@/components/connectors/connection-status";
import { chatThreadLink } from "@/lib/agent-chat-routes";
import type { ActivityCard } from "@/lib/api/activity";
import type { AutomationRecord } from "@/lib/api/automations";
import type {
	ConnectionItem,
	ConnectorDefinitionItem,
	FeedItem,
} from "@/lib/api/connections";
import type { RecentItem } from "@/lib/api/recent-activity";
import {
	automationStatus,
	latestStamp,
} from "@/lib/automations/automation-status";
import { deriveAutomation } from "@/lib/automations/model";
import type { ClientGroup } from "@/lib/connectors/client-rows";
import type { SourceAttribution } from "@/lib/source-attribution";
import type { TimelineItem } from "./attention-inbox";

/** Home lists sources, never items: what ran, what synced, who acted. */
export type HomeSourceKind = "automation" | "connection" | "agent";

/** An attention item and the card it came from (the card carries the source ids). */
export type HomeAttention = {
	card: ActivityCard;
	item: TimelineItem;
	/** Identical plain notices folded into this, the newest; 1 when unique. */
	repeats: number;
	/** Unread notifications this entry stands for, its folded repeats included. */
	unreadIds: number[];
};

export type HomeSource = {
	/** `<kind>:<id>`: what attention cards attach to. */
	key: string;
	/** More keys for the same source: an app registered under several clients. */
	alsoKeys?: string[];
	kind: HomeSourceKind;
	name: string;
	/** One plain-language line: the item needing you, the problem, or the latest activity. */
	status: string;
	/** Latest activity, ms; null when the source never did anything. */
	at: number | null;
	problem: boolean;
	attention: HomeAttention[];
	/** Icon identity for the shared source mark. */
	mark: SourceAttribution;
	/** Automations and connections open in the peek; agents open their page. */
	open:
		| { kind: "automation"; href: string }
		| {
				kind: "connection";
				href: string;
				connectorKey: string;
				connectionId: number;
				section: "feeds" | "actions";
		  }
		| { kind: "page"; href: string };
};

export type HomeSection = {
	kind: HomeSourceKind;
	label: string;
	/**
	 * Every problem and item needing you, then the latest activity up to
	 * SECTION_ROWS rows; the rest are one "See all" away.
	 */
	rows: HomeSource[];
	count: number;
};

/** Rows a section shows before "See all", unless problems need more. */
const SECTION_ROWS = 5;

const SECTIONS: { kind: HomeSourceKind; label: string }[] = [
	{ kind: "automation", label: "Automations" },
	{ kind: "connection", label: "Connections" },
	{ kind: "agent", label: "Agents" },
];

function automationSource(
	owner: string,
	record: AutomationRecord,
	now: number,
): HomeSource {
	const { text, problem, at } = automationStatus(deriveAutomation(record), now);
	return {
		key: `automation:${record.automation_id}`,
		kind: "automation",
		name: record.name,
		status: text,
		at,
		problem,
		attention: [],
		mark: { automation_id: Number(record.automation_id) || null },
		open: {
			kind: "automation",
			href: `/${owner}/automations/${encodeURIComponent(record.automation_id)}?view=activity`,
		},
	};
}

function connectionSource(
	owner: string,
	connection: ConnectionItem,
	feeds: FeedItem[],
	connector: ConnectorDefinitionItem | undefined,
	now: number,
): HomeSource {
	const { text, problem, at } = connectionStatus(
		connection,
		feeds,
		connector,
		now,
	);
	const feedCount = feeds.length || connection.feed_count || 0;
	const name =
		connection.display_name?.trim() ||
		connection.connector_name?.trim() ||
		connection.connector_key;
	return {
		key: `connection:${connection.id}`,
		kind: "connection",
		name,
		status: text,
		at,
		problem,
		attention: [],
		mark: {
			platform: connection.connector_key,
			connection_id: connection.id,
			connection_name: name,
		},
		open: {
			kind: "connection",
			href: `/${owner}/connectors/${encodeURIComponent(connection.connector_key)}/${connection.id}`,
			connectorKey: connection.connector_key,
			connectionId: connection.id,
			section:
				feedCount === 0 && connection.has_operations ? "actions" : "feeds",
		},
	};
}

/** One row per connected client or agent, from its newest conversation. */
type RecentThread = Extract<RecentItem, { kind: "conversation" }>["thread"];
type RecentScope = Extract<RecentItem, { kind: "mcp" }>["conversation"];

/**
 * One row per agent and per connected app: the same roster the Agents page
 * lists. Recent conversations only add a row's latest line and time.
 */
function agentSources(
	owner: string,
	roster: Array<{ agentId: string; name: string }>,
	clientGroups: ClientGroup[],
	items: RecentItem[],
): HomeSource[] {
	const latestThread = new Map<string, RecentThread>();
	const latestScope = new Map<string, RecentScope>();
	for (const item of [...items].sort((a, b) => b.at - a.at)) {
		if (item.kind === "conversation") {
			if (!latestThread.has(item.thread.agentId))
				latestThread.set(item.thread.agentId, item.thread);
		} else if (!latestScope.has(item.conversation.clientId)) {
			latestScope.set(item.conversation.clientId, item.conversation);
		}
	}
	const agents = roster.map((agent): HomeSource => {
		const t = latestThread.get(agent.agentId);
		const platform = t?.platform || "web";
		let href = `/${owner}/agents/${encodeURIComponent(agent.agentId)}`;
		if (t && platform === "web") {
			const chat = chatThreadLink(owner, t.agentId, t.id);
			href = Object.entries(chat.params).reduce(
				(path, [key, value]) =>
					path.replace(`$${key}`, encodeURIComponent(value)),
				chat.to as string,
			);
		} else if (t) {
			href = `/${owner}/agents/${encodeURIComponent(t.agentId)}/conversations/${encodeURIComponent(t.conversationId ?? t.id)}`;
		}
		return {
			key: `agent:${agent.agentId}`,
			kind: "agent",
			name: agent.name || agent.agentId,
			status: t
				? `${t.title || "Conversation"}${platform === "web" ? "" : ` · ${platform}`}${t.isRunning ? " · Active" : ""}`
				: "No recent conversations",
			at: t?.updatedAt ?? null,
			problem: false,
			attention: [],
			mark: { agent_id: agent.agentId, agent_name: agent.name },
			open: { kind: "page", href },
		};
	});
	const apps = clientGroups.map((group): HomeSource => {
		const scope = group.clientIds
			.map((id) => latestScope.get(id))
			.find((match) => match != null);
		const calls = scope
			? `${scope.callCount} ${scope.callCount === 1 ? "call" : "calls"}${scope.failedCount > 0 ? ` · ${scope.failedCount} failed` : ""} in its latest session${scope.isLive ? " · Active" : ""}`
			: null;
		const href =
			owner === "me"
				? `/me/events?${new URLSearchParams({ client_ids: group.clientId })}`
				: `/${owner}/agents/connected/${encodeURIComponent(group.clientId)}`;
		return {
			key: `client:${group.clientId}`,
			alsoKeys: group.clientIds.map((id) => `client:${id}`),
			kind: "agent",
			name: group.title,
			status:
				calls ??
				`Connected app${group.drivenBy ? ` · via ${group.drivenBy}` : ""}`,
			at: latestStamp(group.lastSeenAt, scope?.lastCallAt ?? null),
			problem: false,
			attention: [],
			mark: { client_id: group.clientId, client_name: group.title },
			open: { kind: "page", href },
		};
	});
	return [...agents, ...apps];
}

/**
 * A source that re-sends the same notice every run (a watchdog, a stalled
 * connection) shows it once, newest first, with how many times it repeated.
 * Decisions never fold: each approval or question is its own answer.
 */
export function foldRepeatedNotices(
	newestFirst: HomeAttention[],
): HomeAttention[] {
	const folded: HomeAttention[] = [];
	const byNotice = new Map<string, HomeAttention>();
	for (const entry of newestFirst) {
		const key = noticeKey(entry.card);
		const kept = key ? byNotice.get(key) : undefined;
		if (kept) {
			kept.repeats += 1;
			kept.unreadIds.push(...entry.unreadIds);
			continue;
		}
		if (key) byNotice.set(key, entry);
		folded.push(entry);
	}
	return folded;
}

/** A plain notice's identity for folding: its subject and title. Decisions
 *  (a run, a question) have none; each is its own item. */
function noticeKey(card: ActivityCard): string | null {
	if (card.run_id != null || card.interaction_type != null) return null;
	return `${attentionKeys(card)[0] ?? ""}|${card.title}`;
}

/** How many items Needs you lists, repeats folded: Home's count and badge. */
export function countNeedsYou(cards: ActivityCard[]): number {
	const notices = new Set<string>();
	let decisions = 0;
	for (const card of cards) {
		const key = noticeKey(card);
		if (key) notices.add(key);
		else decisions += 1;
	}
	return notices.size + decisions;
}

/** The source rows an attention card belongs to, most specific first. */
function attentionKeys(card: ActivityCard): string[] {
	return [
		card.automation_id != null ? `automation:${card.automation_id}` : null,
		card.connection_id != null ? `connection:${card.connection_id}` : null,
		card.client_id ? `client:${card.client_id}` : null,
		card.agent_id ? `agent:${card.agent_id}` : null,
	].filter((key): key is string => key != null);
}

function bySeverityThenTime(a: HomeSource, b: HomeSource): number {
	const flagged = (source: HomeSource) =>
		source.attention.length > 0 || source.problem ? 1 : 0;
	return (
		flagged(b) - flagged(a) ||
		(b.at ?? 0) - (a.at ?? 0) ||
		a.name.localeCompare(b.name)
	);
}

export function buildHomeSources({
	owner,
	automations,
	connections,
	feeds,
	connectors,
	recentItems,
	roster,
	clientGroups,
	attention,
	now,
}: {
	owner: string;
	automations: AutomationRecord[];
	connections: ConnectionItem[];
	feeds: FeedItem[];
	connectors: Map<string, ConnectorDefinitionItem>;
	recentItems: RecentItem[];
	roster: Array<{ agentId: string; name: string }>;
	clientGroups: ClientGroup[];
	attention: Array<Omit<HomeAttention, "repeats" | "unreadIds">>;
	now: number;
}): { sources: HomeSource[]; unattached: HomeAttention[] } {
	const feedsByConnection = new Map<number, FeedItem[]>();
	for (const feed of feeds) {
		const list = feedsByConnection.get(feed.connection_id) ?? [];
		list.push(feed);
		feedsByConnection.set(feed.connection_id, list);
	}
	const sources = [
		...automations.map((record) => automationSource(owner, record, now)),
		...connections.map((connection) =>
			connectionSource(
				owner,
				connection,
				feedsByConnection.get(connection.id) ?? [],
				connectors.get(connection.connector_key),
				now,
			),
		),
		...agentSources(owner, roster, clientGroups, recentItems),
	];
	const byKey = new Map(
		sources.flatMap((source) =>
			[source.key, ...(source.alsoKeys ?? [])].map(
				(key) => [key, source] as const,
			),
		),
	);
	const unattached: HomeAttention[] = [];
	for (const input of attention) {
		const { notificationId, unread } = input.item;
		const entry = {
			...input,
			repeats: 1,
			unreadIds: unread && notificationId != null ? [notificationId] : [],
		};
		const source = attentionKeys(entry.card)
			.map((key) => byKey.get(key))
			.find((match) => match != null);
		if (!source) {
			unattached.push(entry);
			continue;
		}
		source.attention.push(entry);
	}
	for (const source of sources) {
		if (source.attention.length === 0) continue;
		source.attention.sort((a, b) => b.item.at - a.item.at);
		source.at = latestStamp(source.at, source.attention[0].item.at);
		source.attention = foldRepeatedNotices(source.attention);
	}
	// Notices about no listed source fold their repeats the same way.
	unattached.sort((a, b) => b.item.at - a.item.at);
	return { sources, unattached: foldRepeatedNotices(unattached) };
}

/**
 * The three fixed sections, always in this order. While a search or the
 * Needs-you filter is on, every match shows and sections with no match drop
 * out.
 */
export function groupHomeSections(
	sources: HomeSource[],
	{ filtering, kinds }: { filtering: boolean; kinds: HomeSourceKind[] },
): HomeSection[] {
	return SECTIONS.filter(({ kind }) => kinds.includes(kind)).flatMap(
		({ kind, label }) => {
			const all = sources
				.filter((source) => source.kind === kind)
				.sort(bySeverityThenTime);
			if (filtering && all.length === 0) return [];
			const flagged = all.filter(
				(source) => source.problem || source.attention.length > 0,
			).length;
			return [
				{
					kind,
					label,
					rows: filtering ? all : all.slice(0, Math.max(flagged, SECTION_ROWS)),
					count: all.length,
				},
			];
		},
	);
}
